import { commonRequest } from "../../services/ApiCall";
import { Helpers } from "../../services/Helpers";

const baseUrl = () => `${Helpers.URL()}/api/v2/admin/quick-book/services`;
const catalogUrl = () => `${Helpers.URL()}/api/v2/admin/quick-book/catalog`;
const requestsUrl = () => `${Helpers.URL()}/api/v2/admin/quick-book/requests`;
const quickBookAdminUrl = () => `${Helpers.URL()}/api/v2/admin/quick-book`;
const requestUrl = (requestNumber) => `${requestsUrl()}/${encodeURIComponent(requestNumber)}`;
const idempotencyHeaders = (key) => ({ "Idempotency-Key": key });

export const QuickBookService = {
  list: async ({ page, limit, search, mode, status }) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set("search", search);
    if (mode) params.set("mode", mode);
    if (status) params.set("status", status);
    return commonRequest("GET", `${baseUrl()}?${params}`, {}, Helpers.token());
  },
  detail: async (serviceId) => commonRequest("GET", `${baseUrl()}/${serviceId}`, {}, Helpers.token()),
  create: async (data) => commonRequest("POST", baseUrl(), data, Helpers.token()),
  update: async (serviceId, data) => commonRequest("PUT", `${baseUrl()}/${serviceId}`, data, Helpers.token()),
  commercialSettings: async () => commonRequest(
    "GET",
    `${quickBookAdminUrl()}/commercial-settings`,
    {},
    Helpers.token(),
  ),
  updateCommercialSettings: async (data) => commonRequest(
    "PUT",
    `${quickBookAdminUrl()}/commercial-settings`,
    data,
    Helpers.token(),
  ),
  commercialPreview: async (data) => commonRequest(
    "POST",
    `${quickBookAdminUrl()}/service-experts/commercial-preview`,
    data,
    Helpers.token(),
  ),
  catalogOfferings: async (mode) => {
    const limit = 100;
    const maximumItems = 500;
    const collected = [];
    const seen = new Set();
    let lastResponse = null;
    let truncated = false;

    for (let page = 1; page <= Math.ceil(maximumItems / limit); page += 1) {
      const response = await QuickBookService.list({ page, limit, search: "", mode, status: "" });
      if (!(response?.success === 1 || response?.success === "1")) return response;
      lastResponse = response;
      const pageItems = response.data?.items || [];
      pageItems.forEach((item) => {
        if (item.status === "ARCHIVED" || (item.mode && item.mode !== mode) || seen.has(item.serviceId) || collected.length >= maximumItems) return;
        seen.add(item.serviceId);
        collected.push(item);
      });

      const pagination = response.data?.pagination || {};
      const totalPages = Number(pagination.totalPages);
      const totalItems = Number(pagination.totalItems);
      const hasKnownTotalPages = Number.isSafeInteger(totalPages) && totalPages > 0;
      const hasKnownTotalItems = Number.isSafeInteger(totalItems) && totalItems >= 0;
      if (collected.length >= maximumItems) {
        truncated = (hasKnownTotalPages && page < totalPages) || (hasKnownTotalItems && page * limit < totalItems);
        break;
      }
      if (hasKnownTotalPages ? page >= totalPages : hasKnownTotalItems ? page * limit >= totalItems : pageItems.length < limit) break;
      if (page === Math.ceil(maximumItems / limit)) truncated = true;
    }

    return {
      ...lastResponse,
      success: lastResponse?.success ?? 1,
      data: {
        ...(lastResponse?.data || {}),
        items: collected,
        pagination: {
          ...(lastResponse?.data?.pagination || {}),
          loadedItems: collected.length,
          truncated,
        },
      },
    };
  },
  catalog: async (mode) => commonRequest(
    "GET",
    `${catalogUrl()}?${new URLSearchParams({ mode })}`,
    {},
    Helpers.token(),
  ),
  saveCatalogDraft: async (data) => commonRequest(
    "PUT",
    `${catalogUrl()}/draft`,
    data,
    Helpers.token(),
  ),
  publishCatalog: async (data) => commonRequest(
    "POST",
    `${catalogUrl()}/publish`,
    data,
    Helpers.token(),
  ),
  canonicalOptions: async ({ mode, parentSourceType, parentSourceId, sourceType, searchAll, search, page, limit, selectedId }) => {
    const params = new URLSearchParams({ mode });
    if (parentSourceType) params.set("parentSourceType", parentSourceType);
    if (parentSourceId) params.set("parentSourceId", String(parentSourceId));
    if (sourceType) params.set("sourceType", sourceType);
    if (typeof searchAll === "boolean") params.set("searchAll", String(searchAll));
    if (search) params.set("search", search);
    if (page) params.set("page", String(page));
    if (limit) params.set("limit", String(limit));
    if (selectedId) params.set("selectedId", String(selectedId));
    return commonRequest("GET", `${catalogUrl()}/canonical-options?${params}`, {}, Helpers.token());
  },
  canonicalServices: async (search = "") => {
    const params = new URLSearchParams({
      page: "1",
      limit: "50",
      searchKey: search,
      sortBy: "title",
      sortType: "desc",
      compact: "true",
    });
    return commonRequest("GET", `${Helpers.URL()}/api/admin/service-master/list?${params}`, {}, Helpers.token());
  },
  expertSettings: async ({ page, limit, search, status }) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    return commonRequest("GET", `${Helpers.URL()}/api/v2/admin/quick-book/expert-settings?${params}`, {}, Helpers.token());
  },
  updateExpertSetting: async (providerId, data) => commonRequest(
    "PUT",
    `${Helpers.URL()}/api/v2/admin/quick-book/expert-settings/${providerId}`,
    data,
    Helpers.token(),
  ),
  mappings: async ({ page, limit, serviceId, providerId, active }) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (serviceId) params.set("serviceId", serviceId);
    if (providerId) params.set("providerId", providerId);
    if (active !== "") params.set("active", active);
    return commonRequest("GET", `${Helpers.URL()}/api/v2/admin/quick-book/service-experts?${params}`, {}, Helpers.token());
  },
  createMapping: async (data) => commonRequest(
    "POST",
    `${Helpers.URL()}/api/v2/admin/quick-book/service-experts`,
    data,
    Helpers.token(),
  ),
  updateMapping: async (mappingId, data) => commonRequest(
    "PUT",
    `${Helpers.URL()}/api/v2/admin/quick-book/service-experts/${mappingId}`,
    data,
    Helpers.token(),
  ),
  providerServices: async (providerId) => commonRequest(
    "GET",
    `${Helpers.URL()}/api/v2/admin/provider-services/list?page=1&limit=500&providerId=${providerId}&searchKey=`,
    {},
    Helpers.token(),
  ),
  providerDetail: async (providerId) => commonRequest(
    "GET",
    `${Helpers.URL()}/api/admin/user?userId=${providerId}`,
    {},
    Helpers.token(),
  ),
  operationRequests: async ({ page, limit, search, status, mode, paymentState, from, to, sort }) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (mode) params.set("mode", mode);
    if (paymentState) params.set("paymentState", paymentState);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (sort) params.set("sort", sort);
    return commonRequest("GET", `${requestsUrl()}?${params}`, {}, Helpers.token());
  },
  searchHistory: async ({ page = 1, limit = 20, search = "", flow = "", eventType = "", from = "", to = "" } = {}) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set("search", search);
    if (flow) params.set("flow", flow);
    if (eventType) params.set("eventType", eventType);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return commonRequest(
      "GET",
      `${quickBookAdminUrl()}/search-history?${params}`,
      {},
      Helpers.token(),
    );
  },
  operationRequest: async (requestNumber) => commonRequest(
    "GET",
    requestUrl(requestNumber),
    {},
    Helpers.token(),
  ),
  operationSuggestions: async (requestNumber, { page, limit }) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    return commonRequest(
      "GET",
      `${requestUrl(requestNumber)}/suggestions?${params}`,
      {},
      Helpers.token(),
    );
  },
  assignConfirmed: async (requestNumber, data, idempotencyKey) => commonRequest(
    "POST",
    `${requestUrl(requestNumber)}/assign-confirmed`,
    data,
    Helpers.token(),
    null,
    idempotencyHeaders(idempotencyKey),
  ),
  cancelOperationRequest: async (requestNumber, data, idempotencyKey) => commonRequest(
    "POST",
    `${requestUrl(requestNumber)}/cancel`,
    data,
    Helpers.token(),
    null,
    idempotencyHeaders(idempotencyKey),
  ),
  callOperationProvider: async (requestNumber, providerId, idempotencyKey) => commonRequest(
    "POST",
    `${requestUrl(requestNumber)}/call`,
    { providerId },
    Helpers.token(),
    null,
    idempotencyHeaders(idempotencyKey),
  ),
};
